"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Divider,
} from "@mui/material"

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-template-tabpanel-${index}`}
      aria-labelledby={`email-template-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const PaymentRecoveryEmailTemplates = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [templates, setTemplates] = useState([])
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  // Template types specific to payment recovery
  const templateTypes = [
    { id: "dunning_notification", name: "Dunning Notification" },
    { id: "grace_period_notification", name: "Grace Period Notification" },
    { id: "payment_recovered", name: "Payment Recovered" },
    { id: "subscription_canceled", name: "Subscription Canceled" },
  ]

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      // In a real implementation, you would fetch the actual templates
      // For now, we'll create dummy templates based on the template types
      const dummyTemplates = templateTypes.map((type) => ({
        id: type.id,
        name: type.name,
        subject: `Your subscription: ${type.name}`,
        htmlContent: `<p>This is the HTML content for the ${type.name} template.</p>`,
        textContent: `This is the text content for the ${type.name} template.`,
        description: `Email sent to customers during the ${type.name.toLowerCase()} phase.`,
      }))

      setTemplates(dummyTemplates)
      if (dummyTemplates.length > 0) {
        setCurrentTemplate(dummyTemplates[0])
      }
    } catch (error) {
      console.error("Error fetching templates:", error)
      setSnackbar({
        open: true,
        message: "Failed to load email templates",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setCurrentTemplate(templates[newValue])
  }

  const handleTemplateChange = (field, value) => {
    setCurrentTemplate((prev) => {
      if (!prev) return null
      return {
        ...prev,
        [field]: value,
      }
    })
  }

  const handleSaveTemplate = async () => {
    if (!currentTemplate) return

    try {
      setSaving(true)
      // In a real implementation, you would save the template to the server
      // For now, we'll just update the local state
      setTemplates((prevTemplates) =>
        prevTemplates.map((template) => (template.id === currentTemplate.id ? currentTemplate : template)),
      )

      setSnackbar({
        open: true,
        message: "Template saved successfully",
        severity: "success",
      })
    } catch (error) {
      console.error("Error saving template:", error)
      setSnackbar({
        open: true,
        message: "Failed to save template",
        severity: "error",
      })
    } finally {
      setSaving(false)
    }
  }

  const handleResetTemplate = async () => {
    if (!currentTemplate) return

    try {
      // In a real implementation, you would reset the template to its default
      // For now, we'll just show a success message
      setSnackbar({
        open: true,
        message: "Template reset to default",
        severity: "success",
      })
    } catch (error) {
      console.error("Error resetting template:", error)
      setSnackbar({
        open: true,
        message: "Failed to reset template",
        severity: "error",
      })
    }
  }

  const handleSendTestEmail = async () => {
    try {
      // In a real implementation, you would send a test email
      // For now, we'll just show a success message
      setSnackbar({
        open: true,
        message: "Test email sent successfully",
        severity: "success",
      })
    } catch (error) {
      console.error("Error sending test email:", error)
      setSnackbar({
        open: true,
        message: "Failed to send test email",
        severity: "error",
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Payment Recovery Email Templates
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Customize the email templates sent during the payment recovery process
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          {templates.map((template, index) => (
            <Tab key={template.id} label={template.name} />
          ))}
        </Tabs>

        {currentTemplate && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Subject
                </Typography>
                <TextField
                  fullWidth
                  value={currentTemplate.subject || ""}
                  onChange={(e) => handleTemplateChange("subject", e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  value={currentTemplate.description || ""}
                  onChange={(e) => handleTemplateChange("description", e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  HTML Content
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={currentTemplate.htmlContent || ""}
                  onChange={(e) => handleTemplateChange("htmlContent", e.target.value)}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Plain Text Content
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={currentTemplate.textContent || ""}
                  onChange={(e) => handleTemplateChange("textContent", e.target.value)}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Available Variables
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {
                      "{{customer_name}}, {{subscription_id}}, {{plan_name}}, {{amount}}, {{currency}}, {{due_date}}, {{retry_date}}, {{grace_period_end}}"
                    }
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <TextField placeholder="Test Email Recipient" variant="outlined" size="small" sx={{ mr: 2 }} />
                <Button variant="outlined" onClick={handleSendTestEmail}>
                  Send Test Email
                </Button>
              </Box>
              <Box>
                <Button variant="outlined" onClick={handleResetTemplate} sx={{ mr: 2 }}>
                  Reset to Default
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  startIcon={saving && <CircularProgress size={20} color="inherit" />}
                >
                  {saving ? "Saving..." : "Save Template"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default PaymentRecoveryEmailTemplates

